import { useEffect, useRef } from 'react';

import { SelectField } from '../../select';

import type { ComponentProps, ReactNode } from 'react';

const bounded = (value: string | null | undefined, limit = 180) =>
  (value ?? '').replace(/\s+/g, ' ').trim().slice(0, limit);

function controlLabel(control: HTMLElement) {
  return bounded(
    (control.dataset['modelKind'] === 'select' ? control.dataset['modelLabel'] : undefined) ??
      control.getAttribute('aria-label') ??
      control.closest('label')?.textContent ??
      control.textContent,
    120,
  );
}

/** Publish only this simulation's visible state and explicitly scoped controls. */
export function LearningModelSurface({ model, children }: { model: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const identities = new WeakMap<HTMLElement, string>();
    let nextIdentity = 0;
    const update = () => {
      const controls = Array.from(
        root.querySelectorAll<HTMLElement>(
          '[data-model-kind="select"], button, input:not([type="hidden"]), textarea, select',
        ),
      ).filter(
        (control) =>
          !control.closest('[hidden]') &&
          (!control.parentElement?.closest('[data-model-kind="select"]') ||
            control.dataset['modelKind'] === 'select'),
      );
      const inputs: Record<string, string | boolean> = {};
      for (const control of controls.slice(0, 20)) {
        let id = identities.get(control);
        if (!id) {
          id = `control-${nextIdentity++}`;
          identities.set(control, id);
        }
        control.dataset['modelAction'] = id;
        control.dataset['modelLabel'] = controlLabel(control) || id;
        if (control.dataset['modelKind'] === 'select' && !(control instanceof HTMLSelectElement)) {
          if (Object.keys(inputs).length < 12)
            inputs[controlLabel(control)] = bounded(control.dataset['modelValue']);
        } else if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) {
          control.dataset['modelKind'] = 'input';
          if (Object.keys(inputs).length < 12)
            inputs[controlLabel(control)] =
              control instanceof HTMLInputElement && control.type === 'checkbox'
                ? control.checked
                : control.value.slice(0, 500);
        } else if (control instanceof HTMLSelectElement) {
          control.dataset['modelKind'] = 'select';
          if (Object.keys(inputs).length < 12)
            inputs[controlLabel(control)] = control.value.slice(0, 500);
          if (control.dataset['modelValue'] !== control.value)
            control.dataset['modelValue'] = control.value;
          control.dataset['modelOptions'] = JSON.stringify(
            Array.from(control.options)
              .slice(0, 20)
              .map((option) => ({
                value: option.value,
                label: option.label,
              })),
          );
        } else control.dataset['modelKind'] = 'button';
        const pressed = control.getAttribute('aria-pressed');
        if ((pressed === 'true' || pressed === 'false') && Object.keys(inputs).length < 12)
          inputs[controlLabel(control)] = pressed === 'true';
      }
      const state: Record<string, string> = {
        evidence: 'Teaching simulation; bounded visible state, not learner execution',
      };
      const design = root.querySelector<HTMLElement>('[data-design]')?.dataset['design'];
      if (design) state['Selected design'] = bounded(design, 100);
      for (const output of Array.from(
        root.querySelectorAll<HTMLElement>(
          'pre, [role="status"], .cb-memory > div, code.cb-code-line, .cb-record-grid, .study-map-detail, .study-card-question, .study-card-answer, .study-kicker',
        ),
      )
        .filter((node) => !node.closest('[hidden]'))
        .slice(0, 10)) {
        const label = bounded(
          (output.matches('code.cb-code-line')
            ? 'Visible code'
            : output.matches('.cb-record-grid')
              ? (output.getAttribute('aria-label') ?? 'Task representation')
              : undefined) ??
            output.querySelector('dt')?.textContent ??
            output.closest('[role="group"]')?.getAttribute('aria-label') ??
            output.parentElement?.querySelector('h4, small, dt')?.textContent,
          50,
        );
        const text = output.matches('.cb-record-grid')
          ? Array.from(output.children)
              .map((row) =>
                Array.from(row.children)
                  .map((cell) => bounded(cell.textContent, 200))
                  .filter(Boolean)
                  .join(' | '),
              )
              .join('\n')
          : (output.textContent ?? '');
        state[label || `output-${Object.keys(state).length}`] = text.slice(0, 500);
      }
      const active = root.querySelector<HTMLElement>('ol [aria-current="step"]');
      const step = active?.parentElement
        ? Array.from(active.parentElement.children).indexOf(active) + 1
        : undefined;
      const snapshot = { ...(step === undefined ? {} : { step }), inputs, state };
      // Bound complete fields, never emit invalid clipped JSON.
      while (JSON.stringify(snapshot).length > 3000 && Object.keys(state).length > 1)
        delete state[Object.keys(state).at(-1) ?? ''];
      while (JSON.stringify(snapshot).length > 3000 && Object.keys(inputs).length)
        delete inputs[Object.keys(inputs).at(-1) ?? ''];
      root.dataset['modelState'] = JSON.stringify(snapshot);
    };
    const schedule = () => queueMicrotask(update);
    const observer = new MutationObserver(update);
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'aria-pressed',
        'aria-current',
        'disabled',
        'checked',
        'hidden',
        'aria-disabled',
        'data-model-value',
        'data-design',
      ],
    });
    for (const event of ['input', 'change', 'click', 'mio:model-action'])
      root.addEventListener(event, schedule);
    update();
    return () => {
      observer.disconnect();
      for (const event of ['input', 'change', 'click', 'mio:model-action'])
        root.removeEventListener(event, schedule);
    };
  }, [model]);
  return (
    <div ref={ref} data-learning-model={model}>
      {children}
    </div>
  );
}

/** The platform select remains the only UI; the adapter invokes its controlled callback. */
export function ModelSelect(props: ComponentProps<typeof SelectField>) {
  const ref = useRef<HTMLSpanElement>(null);
  const { value, options, onValueChange, disabled } = props;
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const handle = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const detail: unknown = event.detail;
      if (!detail || typeof detail !== 'object' || !('actionId' in detail) || !('value' in detail))
        return;
      if (
        detail.actionId !== root.dataset['modelAction'] ||
        typeof detail.value !== 'string' ||
        disabled
      )
        return;
      const option = options.find(
        (entry) => String(entry.value) === detail.value && !entry.disabled,
      );
      if (!option) return;
      Object.assign(detail, { handled: true });
      onValueChange(detail.value);
    };
    root.addEventListener('mio:model-action', handle);
    return () => root.removeEventListener('mio:model-action', handle);
  }, [disabled, onValueChange, options]);
  return (
    <span
      ref={ref}
      data-model-kind="select"
      data-model-label={props['aria-label']}
      data-model-value={String(value)}
      data-model-options={JSON.stringify(
        options
          .slice(0, 20)
          .filter((option) => !option.disabled)
          .map((option) => ({
            value: String(option.value),
            label: typeof option.label === 'string' ? option.label : String(option.value),
          })),
      )}
      aria-disabled={disabled ? true : undefined}
      className="contents"
    >
      <SelectField {...props} />
    </span>
  );
}
